import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useTranslation } from "react-i18next";

const AboutUsOverlapping = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [mounted, setMounted] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Get cards data from translations (namespace 'A' assumed)
  const cardsData = t("A.cards", { returnObjects: true });

 
  const toggleExpand = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  const getShortText = (text, maxLength = 100) =>
    text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

  return (
    <Box
      sx={{
        minHeight: { xs: "auto", md: "100vh" },
        pb: { xs: 6, md: 0 },
        px: { xs: 3, md: 0 },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        marginTop: "50px",
        borderRadius: "30px",
     
      }}
    >
      {/* Heading */}
      <Typography
        variant={isMdUp ? "h2" : "h4"}
        sx={{
          background: "#CE8238",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontWeight: 700,
          letterSpacing: 3,
          textTransform: "uppercase",
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          mb: 6,
          textAlign: "center",
          width: "100%",
          position: "absolute",
          top: 24,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 20,
          opacity: 0,
          animation: "fadeSlideDown 1s ease forwards",
          display: { xs: "none", md: "block" },
        }}
      >
        {t("Who We Are")}
      </Typography>

      <Box
        sx={{
          position: "relative",
          width: { xs: "100%", md: 2300 },
          height: { xs: "auto", md: 480 },
          zIndex: 10,
          display: isMdUp ? "block" : "flex",
          flexDirection: isMdUp ? "initial" : "column",
          justifyContent: "center",
          alignItems: "center",
          px: { xs: 1, md: 0 },
        }}
      >
        {cardsData.map(
          (
            { title, text, image, backgroundColor = "#CE8238", textColor = "#fff" },
            i
          ) => {
            const offsetX = i * 90;
            const delay = 0.3 + i * 0.25;
            const isExpanded = expandedCard === i;

            return (
              <Box
                key={i}
                className={mounted ? "card-anim" : ""}
                sx={{
                  position: isMdUp ? "absolute" : "relative",
                  top: isMdUp ? 0 : "auto",
                  left: isMdUp ? `${60 + offsetX}px` : "auto",
                  transform: isMdUp ? "translateX(-50%)" : "none",
                  width: { xs: "100%", sm: 500, md: 1250 },
                  maxWidth: 1150,
                  height: { xs: "auto", md: 450 },
                  bgcolor: backgroundColor,
                  color: textColor,
                  borderBottomLeftRadius: "15px",
                  borderTopRightRadius: "15px",
                  borderTopLeftRadius: 100,
                  borderBottomRightRadius: 100,
                  padding: 6,
                  cursor: "pointer",
                  transition: "transform 0.35s ease, box-shadow 0.35s ease",
                  zIndex: 10 + i,
                  animationDelay: `${delay}s`,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  border: "4.5px solid rgba(107, 104, 104, 0.34)",
                  mb: isMdUp ? 0 : 5,
                  "&:hover": {
                    transform: isMdUp
                      ? `translateX(-50%) scale(1.1) translateZ(50px)`
                      : "scale(1.06)",
                    zIndex: 100,
                  },
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    letterSpacing: 3,
                    textTransform: "uppercase",
                    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    color: textColor,
                  }}
                >
                  {title}
                </Typography>

                <Typography
                  sx={{
                    flexGrow: 1,
                    lineHeight: 1.9,
                    fontSize: 18,
                    opacity: 0.95,
                    fontFamily: "'Roboto', sans-serif",
                    letterSpacing: 0.6,
                    color: textColor,
                  }}
                >
                  {isExpanded ? text : getShortText(text)}
                </Typography>

                <Button
                  variant="text"
                  onClick={() => toggleExpand(i)}
                  sx={{
                    mt: 2,
                    color: textColor,
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: 14,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {isExpanded ? t("Show Less") : t("Show More")}
                </Button>

                {image && (
                  <Box
                    sx={{
                      mt: 4,
                      width: "100%",
                      height: 550,
                      overflow: "hidden",
                      borderRadius: 2,
                      position: "relative",
                    }}
                  >
                    <Box
                      component="img"
                      src={image}
                      alt={title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "20px",
                      }}
                    />
                    {/* Dark transparent overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        borderRadius: "20px",
                        pointerEvents: "none",
                      }}
                    />
                  </Box>
                )}

                {i === cardsData.length - 1 && (
                  <Box
                    sx={{
                      mt: 5,
                      display: "flex",
                      gap: 4,
                      justifyContent: { xs: "center", md: "flex-start" },
                    }}
                  >
                
                  </Box>
                )}
              </Box>
            );
          }
        )}
      </Box>

      {/* Animations */}
      <style>
        {`
          @keyframes slideFadeIn {
            0% {
              opacity: 0;
              transform: translateY(60px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .card-anim {
            animation-name: slideFadeIn;
            animation-duration: 0.7s;
            animation-fill-mode: forwards;
            animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          }

          @keyframes fadeSlideDown {
            0% {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
            100% {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default AboutUsOverlapping;
